// src/features/analytics/searchParams.ts
//
// Typed URL search params for the /analytics layout route (ADR-003 §3).
// Plain validation function — no Zod (forms-only per CLAUDE.md). URL state
// makes views shareable and refresh-stable without a new context/store.

import {
  MAX_RANGE_DAYS,
  SECONDS_PER_DAY,
  clampRangeSpan,
  presetToRange,
  type UtcRange,
} from '@/shared/utils/utcRange';
import type { AnalyticsScopeKind } from '@/lib/constants/analytics';

/** Presets offered by the New Analytics range control. */
export const ANALYTICS_RANGE_PRESETS = [
  'today',
  'last7d',
  'last30d',
  'last90d',
  'ytd',
  'priorYear',
  'custom',
] as const;
export type AnalyticsRangePreset = typeof ANALYTICS_RANGE_PRESETS[number];

export const ANALYTICS_DEFAULT_PRESET = 'last30d' as const;

export interface AnalyticsSearch {
  scope: AnalyticsScopeKind;
  preset: AnalyticsRangePreset;
  /** UTC epoch seconds; present only when preset === 'custom'. */
  from?: number;
  /** UTC epoch seconds (end-exclusive); present only when preset === 'custom'. */
  to?: number;
}

function asEpochSeconds(value: unknown): number | undefined {
  const n = typeof value === 'string' ? Number(value) : value;
  if (typeof n !== 'number' || !Number.isInteger(n) || n <= 0) return undefined;
  return n;
}

/** validateSearch for the /analytics layout route. Invalid input degrades to defaults. */
export function validateAnalyticsSearch(search: Record<string, unknown>): AnalyticsSearch {
  const scope: AnalyticsScopeKind = search.scope === 'network' ? 'network' : 'account';

  let preset: AnalyticsRangePreset = ANALYTICS_RANGE_PRESETS.includes(
    search.preset as AnalyticsRangePreset
  )
    ? (search.preset as AnalyticsRangePreset)
    : ANALYTICS_DEFAULT_PRESET;

  let from: number | undefined;
  let to: number | undefined;
  if (preset === 'custom') {
    from = asEpochSeconds(search.from);
    to = asEpochSeconds(search.to);
    if (from === undefined || to === undefined || from >= to) {
      preset = ANALYTICS_DEFAULT_PRESET;
      from = undefined;
      to = undefined;
    }
  }

  return { scope, preset, from, to };
}

/**
 * Resolve the validated search to a concrete end-exclusive UTC range, applying
 * the product-window floor and the 1095-day gateway cap (custom inputs from a
 * stale/shared URL could exceed either; the picker prevents new ones).
 */
export function resolveAnalyticsRange(
  search: AnalyticsSearch,
  nowSeconds: number,
  floorSeconds: number | null
): UtcRange {
  if (search.preset === 'custom' && search.from !== undefined && search.to !== undefined) {
    let range = clampRangeSpan({ from: search.from, to: search.to }, MAX_RANGE_DAYS);
    if (floorSeconds !== null && range.from < floorSeconds) {
      range = {
        from: Math.min(floorSeconds, range.to - SECONDS_PER_DAY),
        to: range.to,
      };
    }
    return range;
  }
  const preset = search.preset === 'custom' ? ANALYTICS_DEFAULT_PRESET : search.preset;
  return presetToRange(preset, nowSeconds, floorSeconds ?? undefined);
}
